<template>
  <div
    class="upload-module"
    :class="{
      'upload-module--transparent': moduleData.options.use_transparent_background,
      'component-module-wrapper__module-contents--shape-mask': moduleData
        && moduleData.options && moduleData.options.shape
        && moduleData.options.shape !== 'default',
    }"
    tabindex="0"
    :aria-label="$t('Upload')"
  >
    <!-- Loading/Uploading -->
    <LoadingIndicator
      v-if="isUploading
        || (moduleData.options.upload_id && !upload)
        || (moduleData.options.asset_id && !asset)"
      absolute
      class="upload-module__loading"
      :text="loadingIndicatorText"
    />

    <!-- Upload prompt -->
    <div
      class="upload-module__upload-zone"
      v-else-if="!moduleData.options.upload_id && !moduleData.options.asset_id"
    >
      <div v-if="mode === 'VIEW'">
        <div
          class="comet-button comet-button--icon-with-text comet-button--disabled"
        >
          <span class="comet-button__inner">
            <NebulaIcon
              class-override="comet-button__icon comet-button__icon--left"
              symbol-id="upload"
            />
            <span class="comet-button__text">
              {{ $t('Upload') }}
            </span>
          </span>
        </div>
      </div>
      <div v-else>
        <button
          class="comet-button comet-button--primary comet-button--icon-with-text"
          :disabled="isLocked(id)"
          @click="openMyContentUploadModal"
        >
          <span class="comet-button__inner">
            <NebulaIcon
              class-override="comet-button__icon comet-button__icon--left"
              symbol-id="upload"
            />
            <span class="comet-button__text">
              {{ $t('Upload') }}
            </span>
          </span>
        </button>
      </div>
    </div>

    <!-- Error -->
    <ModuleError
      v-else-if="error"
      :asset-id="asset.id || upload.id"
      :error="error"
    />

    <!-- Default render -->
    <MediaViewer
      v-else
      ref="media"
      :active="active"
      :autoplay="isAutoplay"
      :loop="loopVideo"
      :module-data="moduleData"
      :thumbnail-url="thumbnailUrl"
      :media-url="mediaUrl"
      :mode="mode"
      :title="moduleData.options.title"
      :asset-group="assetGroup"
      :description="moduleData.options.description"
      :icon-id="iconId"
      :player-link="usePlayer ? playerLink : ''"
      :scale="scale"
      :show-title="moduleData.options.show_title"
      :show-description="moduleData.options.show_description"
      :show-type="moduleData.options.show_type"
      :type="type"
      :use-transparent-background="moduleData.options.use_transparent_background"
      :video="video"
      :canvas-name="canvasName"
      :is-displayed="isDisplayed"
      :theme-name="themeName"
      :start-time="durationToSeconds('moduleData.options.video_start')"
      :stop-time="durationToSeconds('moduleData.options.video_end')"
      @video-ended="onVideoEnded"
    />
  </div>
</template>

<script>
import { NebulaIcon } from '@discoveryedu/nebula-components';
import { get } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import MediaViewer from '@/components/MediaViewer.vue';
import ModuleError from '@/components/modules/ModuleError.vue';
import { bus } from '@/lib/eventBus';
import assetMixin from '@/mixins/asset';
import { durationToSeconds } from '../../lib/utils';
import * as types from '@/lib/constants/store';
import { getGroupFromFileType, PROCESSING_ASSET_STATUSES } from '@/lib/constants';
import {
  useAssetStore,
  useEditorStore,
  useModalStore,
  useUploadStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'UploadModule',
  components: {
    LoadingIndicator,
    MediaViewer,
    ModuleError,
    NebulaIcon,
  },
  mixins: [assetMixin],
  props: {
    canvasName: {
      type: String,
      required: true,
    },
    id: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
    active: {
      type: Boolean,
      default: false,
    },
    isDisplayed: {
      type: Boolean,
      default: true,
    },
    page: {
      type: Object,
      default: null,
    },
  },
  created() {
    bus.on('editor-canvas-carousel:start-page-change-animation', this.onStartPageChangeAnimation);
  },
  beforeUnmount() {
    bus.off('editor-canvas-carousel:start-page-change-animation', this.onStartPageChangeAnimation);
  },
  watch: {
    isDisplayed() {
      /*
        When this module is no longer displayed (in carousel but not visible),
        then reset audio and video to beginning.
      */
      if (!this.isDisplayed) {
        const { media } = this.$refs;
        if (media) {
          media.reset();
        }
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'editorIsFullscreen',
      'isLocked',
      'currentPage',
      'domains',
    ]),
    ...mapState(useAssetStore, [
      'assets',
    ]),
    ...mapState(useUploadStore, [
      'uploads',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    asset() {
      if (!get(this, 'moduleData.options.asset_id')) return null;
      return this.assets[this.moduleData.options.asset_id];
    },
    assetGroup() {
      if (this.asset) return this.asset.group;
      if (this.upload) return getGroupFromFileType(this.upload.content_type);

      return null;
    },
    error() {
      return get(this, 'upload.error') || get(this, 'asset.error');
    },
    iconId() {
      return this.assetGroup ? `asset-${this.assetGroup}` : 'asset-document';
    },
    isAutoplay() {
      const autoPlay = get(this, 'moduleData.options.auto_play', false);
      return autoPlay && this.isDisplayed;
    },
    loopVideo() {
      const loop = this.editorIsFullscreen && get(this, 'moduleData.options.loop_video', false);
      return loop && this.isDisplayed;
    },
    autoAdvance() {
      const autoAdvance = this.editorIsFullscreen && get(this, 'moduleData.options.auto_advance', false);
      return autoAdvance && this.isDisplayed;
    },
    isUploading() {
      return PROCESSING_ASSET_STATUSES.includes(this.asset?.status?.id);
    },
    loadingIndicatorText() {
      if (PROCESSING_ASSET_STATUSES.includes(this.asset?.status?.id)) {
        return this.$t('Processing. Please wait.');
      }

      return null;
    },
    upload() {
      if (!get(this.moduleData, 'options.upload_id')) return null;
      return this.uploads[this.moduleData.options.upload_id];
    },
    thumbnailUrl() {
      // as of now, videos don't have thumbnails
      if (['video', 'audio'].includes(this.assetGroup)) {
        return null;
      }

      let thumbnailUrl = null;

      if (this.upload) {
        thumbnailUrl = {
          thumbnailLoaderId: this.moduleData.options.upload_id,
          imageUrls: [{
            url: this.upload.url,
            isThumbnail: false,
          }],
        };
      } else if (this.asset) {
        const validMediaFile = get(this, 'asset.media_files', []).find((mediaFile) => mediaFile.format_id !== -1);

        if (validMediaFile) {
          thumbnailUrl = {
            thumbnailLoaderId: this.asset.id,
            imageUrls: [{
              url: validMediaFile.url,
              isThumbnail: false,
            }],
          };
        }
      }

      return thumbnailUrl;
    },
    mediaUrl() {
      // Only audio has a media url
      if (this.assetGroup === 'audio') {
        return get(this, 'upload.url') || get(this, 'asset.media_files[0].url');
      }

      return null;
    },
    type() {
      // This is the human-readable asset type for an upload. For assets (DE-created
      // or UCC), this is available; for old Studio uploads use the asset group, title-cased.
      if (this.asset?.type?.name) return this.asset.type.name;

      if (!this.assetGroup) return null;

      return this.assetGroup.charAt(0).toUpperCase() + this.assetGroup.slice(1);
    },
    video() {
      if (this.assetGroup !== 'video') return undefined;

      if (this.asset) {
        // Videos uploaded to My Content don't need much formatting
        return {
          ...this.asset,
          closed_captions: [],
          duration_in_seconds: this.asset.duration,
        };
      }

      // Videos uploaded using the old Studio upload api need their data rearranged to
      // look like an asset
      return {
        uploadId: this.upload?.id,
        closed_captions: [],
        duration_in_seconds: null,
        media_files: [{
          format: {},
          type: this.upload.content_type,
          url: this.upload.url,
        }],
        title: this.upload.filename,
        type: {
          name: 'Full Video',
        },
      };
    },
    themeName() {
      return (this.page || this.currentPage)?.options?.theme?.name;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_MODULE,
      types.VIEW_NEXT_PAGE,
    ]),
    ...mapActions(useModalStore, [
      types.OPEN_MODAL,
    ]),
    durationToSeconds,
    onVideoEnded() {
      if (this.autoAdvance) {
        this[types.VIEW_NEXT_PAGE]();
      }
    },
    onStartPageChangeAnimation() {
      /*
        When this module is animating out of view, ensure
        that the audio or video is paused.
      */
      const { media } = this.$refs;
      if (media) {
        media.pause();
      }
    },
    openMyContentUploadModal() {
      const vm = this;

      if (this.isDeUser) {
        this[types.OPEN_MODAL]({
          type: 'EditorialUploadInfo',
          allowMultiple: false,
          canvasName: this.canvasName,
          moduleToReplace: this.moduleData,
        });
      } else {
        this[types.OPEN_MODAL]({
          type: 'MyContentUploadModal',
          allowMultiple: false,
          baseURL: this.domains.deDomain,
          onAssetsUploaded(assets) {
            // The upload modal returns an array of assets, but we are only expecting one
            const assetId = assets[0].id;

            vm[types.UPDATE_MODULE]({
              canvasName: vm.canvasName,
              payload: {
                ...vm.moduleData,
                options: {
                  ...vm.moduleData.options,
                  asset_id: assetId,
                  description: undefined,
                  title: undefined,
                  upload_id: undefined,
                },
              },
            });
          },
        });
      }
    },
  },
};
</script>

<style lang="stylus">
.upload-module {
  background-color: $nebula-color-white;
  break-inside: avoid; // Avoid splitting across pages when printing
  height: 100%;

  &--transparent {
    background-color: transparent;
  }

  &__upload-zone {
    align-items: center;
    background-color: $comet-background-color-light;
    border-radius: 6px; // match module styles
    display: flex;
    height: 100%;
    justify-content: center;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;

    .comet-button {
      font-feature-settings: 'liga' 1, 'ss01' 0; // UI buttons do not use schoolbook styles
      margin: 0;
    }
  }

  &__loading { // .upload-module__loading
    border-radius: 6px; // match module styles
    z-index: inherit;
  }
}
</style>
